/**
 * Ключи доступа — ручки auth (`/api/v1/auth/api-keys`), эпик DEV-1518.
 *
 * Клиент написан руками, а не сгенерирован: `npm run generate:api` собирает
 * клиентов из спек соседних клонов tracker и knowledge, а это auth — третий
 * репозиторий, и тянуть его в скрипт ради трёх ручек дороже, чем сорок строк
 * здесь. Появится четвёртая ручка — заведём генерацию по-настоящему.
 */
import { http } from '@/api/http'

/** Права, которыми можно сузить ключ. Тот же закрытый список, что в auth. */
export const KEY_SCOPES = [
  'tracker:read',
  'tracker:write',
  'knowledge:read',
  'knowledge:write',
] as const

export type KeyScope = (typeof KEY_SCOPES)[number]

/** Срок жизни ключа. Значения — enum auth, порядок как в макете (203:995). */
export const KEY_TTLS = [
  'API_KEY_TTL_30_DAYS',
  'API_KEY_TTL_90_DAYS',
  'API_KEY_TTL_180_DAYS',
  'API_KEY_TTL_365_DAYS',
] as const

export type KeyTtl = (typeof KEY_TTLS)[number]

export interface ApiKey {
  id: string
  name: string
  /** Первые символы ключа: отличить один от другого в списке */
  keyPrefix: string
  expiresAt: string
  isActive: boolean
  revokedAt?: string
  lastUsedAt?: string
  createdAt: string
  /** Чем ключ сужен; пусто — ключ работает всеми правами владельца */
  scopes?: string[]
}

/**
 * Как auth отдаёт ключ на проводе.
 *
 * ⚠ Имена полей snake_case, и это не описка: gateway трекера отдаёт camelCase,
 * а auth собран с `UseProtoNames` и отдаёт имена как в proto. Разницу видно
 * только вживую — при чтении camelCase из этого ответа TypeScript молчит,
 * поля просто приходят `undefined`, и живой ключ рисуется истёкшим.
 */
interface ApiKeyWire {
  id: string
  name: string
  key_prefix: string
  expires_at: string
  is_active: boolean
  revoked_at?: string
  last_used_at?: string
  created_at: string
  scopes?: string[]
}

interface ListResponse {
  keys?: ApiKeyWire[]
  active_limit?: number
}

interface CreateResponse {
  key?: ApiKeyWire
  /** Значение ключа. Приходит один раз и нигде больше не появится */
  plaintext_key?: string
}

export interface KeysPage {
  keys: ApiKey[]
  activeLimit: number
}

const toApiKey = (wire: ApiKeyWire): ApiKey => ({
  id: wire.id,
  name: wire.name,
  keyPrefix: wire.key_prefix ?? '',
  expiresAt: wire.expires_at ?? '',
  isActive: wire.is_active ?? false,
  revokedAt: wire.revoked_at,
  lastUsedAt: wire.last_used_at,
  createdAt: wire.created_at ?? '',
  scopes: wire.scopes,
})

/** Список ключей владельца вместе с историей погашенных. */
export const listApiKeys = async (): Promise<KeysPage> => {
  const { data } = await http.get<ListResponse>('/api/v1/auth/api-keys')

  return {
    keys: (data.keys ?? []).map(toApiKey),
    activeLimit: data.active_limit ?? 0,
  }
}

export interface CreateKeyParams {
  name: string
  ttl: KeyTtl
  scopes: KeyScope[]
}

/**
 * Выпускает ключ и возвращает его значение — единственный раз за всю жизнь
 * ключа. Не показали сразу — придётся выпускать новый.
 */
export const createApiKey = async (params: CreateKeyParams): Promise<{ key: ApiKey; secret: string }> => {
  const { data } = await http.post<CreateResponse>('/api/v1/auth/api-keys', {
    name: params.name,
    ttl: params.ttl,
    scopes: params.scopes,
  })

  return {
    key: data.key ? toApiKey(data.key) : ({} as ApiKey),
    secret: data.plaintext_key ?? '',
  }
}

/** Гасит ключ. Следующий вызов агента с ним получит отказ. */
export const revokeApiKey = async (id: string): Promise<void> => {
  await http.delete(`/api/v1/auth/api-keys/${id}`)
}
